import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useScrollTransition } from 'util/react-transition-component';
import { renderCta } from 'util/cta/cta.template';
import type { CtaRefType } from 'util/cta/cta.data';
import Text from '../text/Text';
import {
  setupHoverTransitionTimeline,
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './PrimaryButton.transitions';
import type { PrimaryButtonProps, PrimaryButtonRef } from './PrimaryButton.data';
import styles from './PrimaryButton.module.scss';
import { defaultButtonSize } from './PrimaryButton.data';
import useSetupTimeline from '../../../hooks/useSetupTimeline';
import { isTouch } from '../../../hooks/useIsDesktopDevice';
import Icon from '../icon/Icon';

export default function PrimaryButton({
  isVisible,
  disableTriggerInOnScroll = true,
  revertColor = false,
  hasOutline = false,
  isSelected = false,
  transitionInDelay,
  noTransition = false,
  colorVariation,
  icon,
  ref,
  ...props
}: PrimaryButtonProps & { ref?: Ref<PrimaryButtonRef> }): ReactElement {
  const localRef = useRef<CtaRefType>(undefined);
  const elementRef = (ref || localRef) as RefObject<CtaRefType | null>;
  const labelRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const backgroundHoverRef = useRef<HTMLDivElement>(null);
  const hoverLabelRef = useRef<HTMLDivElement>(null);
  const hoverTimeline = useRef<gsap.core.Timeline>(undefined);
  let reverted = revertColor;
  if (isSelected) {
    reverted = !revertColor;
  }

  hoverTimeline.current = useSetupTimeline(setupHoverTransitionTimeline, {
    backgroundHoverRef,
    hoverLabelRef,
    contentRef,
  });

  const { transitionController } = useScrollTransition({
    scroll: {
      transitionInDelay,
      disableTriggerInOnScroll,
      trigger: elementRef,
    },
    setupOptions: () => ({
      ref: elementRef,
      refs: {
        elementRef,
        labelRef,
        noTransition,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
  });

  useChangeTransitionState(transitionController, isVisible);

  const onMouseLeave = useCallback(() => {
    hoverTimeline?.current?.reverse();
  }, [hoverTimeline]);

  const onMouseEnter = useCallback(() => {
    hoverTimeline?.current?.play();
  }, [hoverTimeline]);

  useEffect(() => {
    if (isSelected) {
      hoverTimeline?.current?.paused(true);
      hoverTimeline?.current?.seek(0);
    }
  }, [isSelected, hoverTimeline]);

  return renderCta({
    innerTemplate: (
      <>
        <div ref={backgroundHoverRef} className={classNames(styles.backgroundHover)} />

        <div className={classNames(styles.textHolder)}>
          <div ref={contentRef} className={classNames(styles.content)}>
            {icon && <Icon name={icon} className={classNames(styles.icon)} />}
            <Text
              ref={labelRef}
              transitionInType={
                noTransition ? 'none' : isTouch ? 'splitWordsSlideInUp' : 'splitCharsSlideInUp'
              }
              className={classNames(styles.label)}
              as="span"
              size="button-label-large"
              enableChineseTransitionFallback={false}
            >
              {props.label}
            </Text>
          </div>
          <div
            ref={hoverLabelRef}
            className={classNames(styles.content, styles.hoverContent)}
            aria-hidden="true"
          >
            {icon && <Icon name={icon} className={classNames(styles.icon)} />}
            <Text
              transitionInType={noTransition ? 'none' : 'splitCharsNone'}
              className={classNames(styles.label)}
              as="span"
              size="button-label-large"
              enableChineseTransitionFallback={false}
            >
              {props.label}
            </Text>
          </div>
        </div>
      </>
    ),
    props: {
      ...props,
      className: classNames(
        props.className,
        styles.primaryButton,
        styles[`size-${props?.size ?? defaultButtonSize}`],
        {
          [styles.revertColor]: reverted,
          [styles.hasOutline]: hasOutline,
          [styles.isSelected]: isSelected,
          [styles[`color-variation-${colorVariation}`]]: !!colorVariation,
        },
      ),
      'aria-pressed': `${isSelected}`,
      onMouseEnter,
      onMouseLeave,
    },
    elementRef,
  });
}
